import { Briefcase, ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { PixelPrince, PixelGuard, PixelSultan, MagicSpell, PixelWrench, PoweredUpSultan } from '../PixelCharacters';

export function CareerSection({ career, loading }) {
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [defeatedGuards, setDefeatedGuards] = useState(new Set());
    const [dyingGuards, setDyingGuards] = useState(new Set());
    const [slashingAt, setSlashingAt] = useState(null);
    const [throwingKnifeAt, setThrowingKnifeAt] = useState(null);
    const [knifePosition, setKnifePosition] = useState({ x: 0, y: 0, targetX: 0, targetY: 0 });
    const [scrollProgress, setScrollProgress] = useState(0);
    const [princeEntered, setPrinceEntered] = useState(false);
    const [princeSection, setPrinceSection] = useState(0);
    const sectionRef = useRef(null);

    // Reverse career to show oldest first (forward-moving story)
    const reversedCareer = [...career].reverse();

    useEffect(() => {
        const handleScroll = () => {
            const section = document.getElementById('career');
            if (!section) return;
            
            const rect = section.getBoundingClientRect();
            const sectionHeight = section.offsetHeight;
            const viewportHeight = window.innerHeight;
            
            // Calculate how far we've scrolled through the section
            const scrolled = Math.max(0, -rect.top + viewportHeight * 0.3);
            const progress = Math.min(0.95, scrolled / (sectionHeight - viewportHeight * 0.3));
            setScrollProgress(progress);
            
            // Calculate which section the prince is at
            const totalSections = reversedCareer.length || 2;
            const currentSection = Math.floor(progress * totalSections);
            setPrinceSection(Math.min(currentSection, totalSections - 1));
            
            // Trigger prince entrance when entering the section
            if (progress > 0.02 && !princeEntered) {
                setPrinceEntered(true);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [reversedCareer.length, princeEntered]);

    const toggleExpand = (id, index, event) => {
        // Stop navbar battle animation when user clicks a career box
        if (window.stopNavBattle) {
            window.stopNavBattle();
        }

        // Check if Prince is at this section
        const isPrinceAtSection = princeSection === index;
        
        // Index 0 is B.Tech (no guard), guards start from index 1
        if (index > 0 && !defeatedGuards.has(index) && !dyingGuards.has(index)) {
            // Start dying animation
            setDyingGuards(prev => {
                const newSet = new Set(prev);
                newSet.add(index);
                return newSet;
            });

            if (isPrinceAtSection) {
                // Prince is at this section - sword slash then guard dies
                setSlashingAt(index);
                setTimeout(() => {
                    setSlashingAt(null);
                    // Guard falls after being struck
                    setTimeout(() => {
                        setDefeatedGuards(prev => {
                            const newSet = new Set(prev);
                            newSet.add(index);
                            return newSet;
                        });
                    }, 600);
                }, 200);
            } else {
                // Prince is not at this section - throw knife at guard
                // Guards are on the OPPOSITE side of the card
                // Even index cards are on the RIGHT, so guards are on LEFT (throw left)
                // Odd index cards are on the LEFT, so guards are on RIGHT (throw right)
                const isGuardOnLeft = index % 2 === 0;
                
                setKnifePosition({
                    targetX: isGuardOnLeft ? -120 : 120,
                    targetY: 0,
                    direction: isGuardOnLeft ? 'left' : 'right'
                });
                
                setThrowingKnifeAt(index);
                
                // Knife hits guard, then guard falls
                setTimeout(() => {
                    setThrowingKnifeAt(null);
                    // Guard falls after being hit by knife
                    setTimeout(() => {
                        setDefeatedGuards(prev => {
                            const newSet = new Set(prev);
                            newSet.add(index);
                            return newSet;
                        });
                    }, 500);
                }, 350);
            }
        }
        
        // Toggle expand
        setExpandedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    // Placeholder data if no career entries
    const placeholderCareer = [
        {
            id: '1',
            title: 'B.Tech',
            company: 'SRM University',
            period: '2012 - 2016',
            description: '',
            order: 0
        },
        {
            id: '2',
            title: 'Current Position',
            company: 'Current Company',
            period: '2023 - Present',
            description: 'The journey continues...',
            order: 1
        }
    ];

    const displayCareer = reversedCareer.length > 0 ? reversedCareer : placeholderCareer;
    
    // Check if this is the B.Tech section (first entry after reverse)
    const isBTechSection = (index) => index === 0;
    
    // Check if all guards are defeated (excluding B.Tech which has no guard)
    const allGuardsDefeated = defeatedGuards.size === displayCareer.length - 1 && displayCareer.length > 1;

    // Parse description into bullet points
    const parseDescription = (description) => {
        if (!description) return [];
        // Split by bullet character •
        const bullets = description.split('•').map(b => b.trim()).filter(b => b);
        return bullets;
    };

    return (
        <section 
            id="career" 
            className="py-16 sm:py-24 bg-background"
            data-testid="career-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="mb-12 sm:mb-16">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-primary flex items-center justify-center">
                            <Briefcase className="h-4 w-4 text-primary-foreground" />
                        </div>
                        <h2 
                            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight uppercase"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                            data-testid="career-title"
                        >
                            Professional Journey
                        </h2>
                    </div>
                    <p className="text-muted-foreground max-w-2xl">
                        The path travelled, battles fought, XP gained :)
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative">
                    {/* Vertical line */}
                    <div className="absolute left-[7px] md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-border" />
                    
                    {/* MOBILE: Prince character following scroll - positioned on left side */}
                    <div 
                        className="md:hidden absolute left-0 z-20 transition-all duration-500 ease-out"
                        style={{ 
                            top: `${Math.max(0, scrollProgress * 100 - 5)}%`,
                        }}
                    >
                        <div className="relative" style={{ transform: 'scale(0.7)', transformOrigin: 'top left' }}>
                            <PixelPrince 
                                fighting={princeEntered && scrollProgress > 0.1} 
                                slashing={slashingAt !== null}
                                throwingKnife={throwingKnifeAt !== null}
                                knifeDirection="right"
                                entering={!princeEntered || scrollProgress < 0.05}
                            />
                            {/* Sword swing effect when slashing */}
                            {slashingAt !== null && (
                                <div className="absolute -right-4 top-0 w-8 h-2 bg-gradient-to-r from-white via-yellow-200 to-transparent opacity-80 animate-pulse" 
                                     style={{ transform: 'rotate(-30deg)', transformOrigin: 'left center' }} />
                            )}
                        </div>
                    </div>

                    {/* MOBILE: Sultan running away from Prince - ahead of Prince on timeline */}
                    {displayCareer.length > 1 && princeSection >= 1 && !allGuardsDefeated && (
                        <div 
                            className="md:hidden absolute z-30 transition-all duration-500 ease-out"
                            style={{ 
                                top: `${Math.min(95, Math.max(10, (scrollProgress * 100) + 15))}%`,
                                right: '10px',
                            }}
                        >
                            <div className="relative" style={{ transform: 'scale(0.6) scaleX(-1)', transformOrigin: 'top right' }}>
                                <PixelSultan castingSpell={true} className="sultan-casting" />
                                {/* Lightning effect */}
                                <div className="absolute left-1/2 -translate-x-1/2 top-10">
                                    <MagicSpell className="lightning-flash" style={{ transform: 'scale(0.7)' }} />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* DESKTOP: Prince character following scroll */}
                    <div 
                        className="hidden md:block absolute left-1/2 -translate-x-1/2 z-20 transition-all duration-500 ease-out"
                        style={{ 
                            top: `${Math.max(0, scrollProgress * 100 - 5)}%`,
                        }}
                    >
                        <div className="relative">
                            <PixelPrince 
                                fighting={princeEntered && scrollProgress > 0.1} 
                                slashing={slashingAt !== null}
                                throwingKnife={throwingKnifeAt !== null}
                                knifeDirection={knifePosition.direction || 'right'}
                                entering={!princeEntered || scrollProgress < 0.05}
                                className="transform -translate-x-8" 
                            />
                            {/* Sword swing effect when slashing */}
                            {slashingAt !== null && (
                                <div className="absolute -right-4 top-0 w-8 h-2 bg-gradient-to-r from-white via-yellow-200 to-transparent opacity-80 animate-pulse" 
                                     style={{ transform: 'rotate(-30deg)', transformOrigin: 'left center' }} />
                            )}
                        </div>
                    </div>

                    {/* DESKTOP: Sultan that moves with Prince - starts from index 1 (Mu Sigma) */}
                    {displayCareer.length > 1 && princeSection >= 1 && !allGuardsDefeated && (
                        <div 
                            className="hidden md:block absolute z-30 transition-all duration-500 ease-out"
                            style={{ 
                                top: `${Math.max(8, (scrollProgress * 100) - 8)}%`,
                                left: princeSection % 2 === 1 ? '15%' : '75%',
                            }}
                        >
                            <div className="relative">
                                <PixelSultan castingSpell={true} className="sultan-casting" />
                                {/* Lightning/spell coming down */}
                                <div className="absolute left-1/2 -translate-x-1/2 top-12">
                                    <MagicSpell className="lightning-flash" />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-12">
                        {loading ? (
                            // Loading skeleton
                            Array(2).fill(0).map((_, i) => (
                                <div key={i} className="relative flex flex-col md:flex-row gap-8">
                                    <div className="md:w-1/2 md:text-right md:pr-12">
                                        <div className="h-4 w-24 bg-muted skeleton-pulse ml-auto hidden md:block" />
                                    </div>
                                    <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-4 h-4 bg-muted" />
                                    <div className="md:w-1/2 md:pl-12 pl-8">
                                        <div className="retro-card p-6 space-y-3">
                                            <div className="h-6 w-48 bg-muted skeleton-pulse" />
                                            <div className="h-4 w-32 bg-muted skeleton-pulse" />
                                            <div className="h-16 bg-muted skeleton-pulse" />
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            displayCareer.map((item, index) => (
                                <div 
                                    key={item.id}
                                    className={`relative flex flex-col md:flex-row gap-8 ${
                                        index % 2 === 0 ? '' : 'md:flex-row-reverse'
                                    }`}
                                    data-testid={`career-item-${item.id}`}
                                >
                                    {/* Guard character - Desktop: NO guard at B.Tech (index 0), guards start from index 1 */}
                                    {!isBTechSection(index) && !defeatedGuards.has(index) && (
                                        <div className={`hidden md:block absolute ${
                                            index % 2 === 0 ? 'right-[52%]' : 'left-[52%]'
                                        } top-1/2 -translate-y-1/2 z-10 ${
                                            dyingGuards.has(index) ? 'guard-fall-death' : ''
                                        } ${slashingAt === index ? 'guard-struck' : ''}`}>
                                            <PixelGuard 
                                                defeated={false}
                                                variant={index}
                                                className={index % 2 === 0 ? '' : 'transform -scale-x-100'}
                                            />
                                            {/* Knife hitting guard */}
                                            {throwingKnifeAt === index && (
                                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 knife-hit">
                                                    <svg width="16" height="16" viewBox="0 0 8 8" style={{ imageRendering: 'pixelated' }}>
                                                        <rect x="1" y="3" width="6" height="2" fill="#C0C0C0"/>
                                                        <rect x="0" y="3" width="2" height="2" fill="#DC2626"/>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* MOBILE Guard - positioned to the right of the card */}
                                    {!isBTechSection(index) && !defeatedGuards.has(index) && (
                                        <div className={`md:hidden absolute -right-2 top-1/2 -translate-y-1/2 z-10 ${
                                            dyingGuards.has(index) ? 'guard-fall-death' : ''
                                        } ${slashingAt === index || throwingKnifeAt === index ? 'guard-struck' : ''}`}
                                        style={{ transform: 'scale(0.6) translateY(-50%) scaleX(-1)', transformOrigin: 'right center' }}>
                                            <PixelGuard 
                                                defeated={false}
                                                variant={index}
                                            />
                                            {/* Knife hitting guard on mobile */}
                                            {throwingKnifeAt === index && (
                                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 knife-hit">
                                                    <svg width="12" height="12" viewBox="0 0 8 8" style={{ imageRendering: 'pixelated' }}>
                                                        <rect x="1" y="3" width="6" height="2" fill="#C0C0C0"/>
                                                        <rect x="0" y="3" width="2" height="2" fill="#DC2626"/>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Date - Desktop */}
                                    <div className={`hidden md:block md:w-1/2 ${
                                        index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'
                                    }`}>
                                        <span 
                                            className="text-xs uppercase tracking-[0.2em] text-primary font-bold"
                                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                        >
                                            {item.period}
                                        </span>
                                    </div>

                                    {/* Timeline Node */}
                                    <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 timeline-node" />

                                    {/* Content Card */}
                                    <div className={`md:w-1/2 pl-8 md:pl-0 ${
                                        index % 2 === 0 ? 'md:pl-12' : 'md:pr-12'
                                    }`}>
                                        <div 
                                            className="retro-card p-6 hover:border-primary transition-all cursor-pointer"
                                            onClick={(e) => toggleExpand(item.id, index, e)}
                                            data-testid={`career-card-${item.id}`}
                                        >
                                            {/* Mobile date */}
                                            <span 
                                                className="md:hidden text-xs uppercase tracking-[0.2em] text-primary font-bold block mb-2"
                                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                            >
                                                {item.period}
                                            </span>

                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <h3 
                                                            className="text-xl font-bold mb-1"
                                                            style={{ fontFamily: "'Silkscreen', cursive" }}
                                                        >
                                                            {item.title}
                                                        </h3>
                                                        {/* Wrench for B.Tech */}
                                                        {isBTechSection(index) && <PixelWrench />}
                                                    </div>
                                                    <p className="text-sm text-primary font-bold uppercase tracking-widest">
                                                        {item.company}
                                                    </p>
                                                </div>
                                                
                                                {/* Only show chevron if there's a description and not B.Tech */}
                                                {!isBTechSection(index) && item.description && (
                                                    <ChevronDown 
                                                        className={`h-5 w-5 text-muted-foreground transition-transform duration-200 flex-shrink-0 mt-1 ${
                                                            expandedIds.has(item.id) ? 'rotate-180' : ''
                                                        }`}
                                                    />
                                                )}
                                            </div>
                                            
                                            {/* Expandable description with gem bullets - NOT for B.Tech section */}
                                            {!isBTechSection(index) && item.description && (
                                                <div 
                                                    className={`overflow-hidden transition-all duration-300 ${
                                                        expandedIds.has(item.id) ? 'max-h-96 mt-3 opacity-100' : 'max-h-0 opacity-0'
                                                    }`}
                                                >
                                                    <ul className="gem-bullet text-muted-foreground text-sm border-t border-dashed border-border pt-3">
                                                        {parseDescription(item.description).map((bullet, i) => (
                                                            <li key={`${item.id}-bullet-${i}`}>{bullet.trim()}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Next Boss Fight Box - shows when all guards defeated */}
                    {allGuardsDefeated && (
                        <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-24 justify-center">
                            <div 
                                className="text-center px-8 py-6 border-4 border-primary bg-card relative overflow-hidden"
                                style={{ fontFamily: "'Silkscreen', cursive" }}
                            >
                                {/* Powered up Sultan in the box */}
                                <div className="absolute -right-4 -top-2">
                                    <PoweredUpSultan />
                                </div>
                                
                                {/* Magic effects */}
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-red-500/10 to-purple-500/10 animate-pulse" />
                                
                                <p className="text-primary text-lg uppercase tracking-widest relative z-10">
                                    You're Ready For The
                                </p>
                                <p className="text-2xl uppercase tracking-widest text-destructive relative z-10 mt-1">
                                    Next Boss Fight
                                </p>
                                <p className="text-xs text-muted-foreground mt-2 relative z-10">
                                    The Sultan grows stronger...
                                </p>
                                
                                {/* Lightning effects */}
                                <div className="absolute left-4 top-4">
                                    <MagicSpell className="lightning-flash opacity-50" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
